/**
 * CihReviewerByline — the "Reviewed by {name}, {credentials} · {date}" strip
 * shared by the V3 Guide and Playbook detail templates.
 *
 * A plain left-aligned gray band that matches the green coaching-business
 * theme. Deliberately NOT the challenge-side `TrustSignalBadge`: that renders a
 * purple pill / dark uppercase strip tuned to the Healthline challenge hero and
 * returns null without a reviewer name, whereas V3 bylines also render with only
 * credentials or only a date. Renders nothing when all three fields are absent.
 *
 * `reviewedDate` is a free-text field carrying month-level precision by design
 * (e.g. "June 2026") and is rendered verbatim — never parsed into a Date. Parsing
 * would fabricate a day-of-month the data doesn't have (JS reads "July 2026" as
 * July 1), displaying a precision that was never entered.
 *
 * Aliases: reviewer byline, reviewed by strip, cih byline, guide/playbook byline
 */

type Props = {
  readonly reviewerName: string | null;
  readonly reviewerCredentials: string | null;
  readonly reviewedDate: string | null;
};

export function CihReviewerByline({
  reviewerName,
  reviewerCredentials,
  reviewedDate,
}: Props) {
  if (
    reviewerName == null &&
    reviewerCredentials == null &&
    reviewedDate == null
  ) {
    return null;
  }

  return (
    <div className="border-b border-gray-200">
      <div className="mx-auto max-w-7xl px-4 py-5 xl:px-0">
        <p className="text-sm text-gray-600">
          {reviewerName ? (
            <>
              Reviewed by{" "}
              <span className="font-medium text-gray-900">{reviewerName}</span>
              {reviewerCredentials ? `, ${reviewerCredentials}` : null}
            </>
          ) : reviewerCredentials ? (
            <>Reviewed — {reviewerCredentials}</>
          ) : null}
          {reviewedDate ? (
            <span className="text-gray-400">
              {reviewerName || reviewerCredentials ? " · " : "Reviewed "}
              {reviewedDate}
            </span>
          ) : null}
        </p>
      </div>
    </div>
  );
}
